import { ConnectionState } from './types';

/**
 * The realtime connection, said out loud (mapd-issues#664).
 *
 * ADR-0004 removed Gala Seating's background poll and promised a connection
 * indicator in its place, so that a socket which never connects is visible
 * rather than silent. This is that promise's wording, kept apart from the
 * component that draws it so the copy can be asserted on directly.
 *
 * Two rules shape it. The state is always named in words, because colour alone
 * is not a channel every admin has. And a state that is not `connected` always
 * says what it means for the room in front of the admin — "out of date" —
 * rather than reporting on a socket they did not know existed.
 */
export interface SeatConnectionStatus {
  state: ConnectionState;
  /** Chakra colour scheme. Reinforces `label`; never carries it alone. */
  tone: 'green' | 'orange' | 'red';
  /** The word on the canvas. */
  label: string;
  /** What this state means for the room the admin is looking at. */
  detail: string;
  /** Whether what is on screen may no longer be what the backend holds. */
  stale: boolean;
  /** When the room was last refetched, or null if it never has been. */
  syncedAtLabel: string | null;
  /** The whole sentence, for the focusable element's accessible name. */
  ariaLabel: string;
}

const STATES: Record<
  ConnectionState,
  Omit<SeatConnectionStatus, 'state' | 'syncedAtLabel' | 'ariaLabel'>
> = {
  connected: {
    tone: 'green',
    label: 'Live',
    detail:
      'Live updates are on. Seat Assignments made in the Backend Portal appear here as they happen.',
    stale: false,
  },
  connecting: {
    tone: 'orange',
    label: 'Connecting…',
    detail:
      'Live updates have not started yet. Seat Assignments made elsewhere are not arriving, so this room may be out of date.',
    stale: true,
  },
  disconnected: {
    tone: 'red',
    label: 'Offline',
    detail:
      'Live updates have stopped. Seat Assignments made elsewhere are not arriving, so this room may be out of date. It refreshes by itself when the connection comes back.',
    stale: true,
  },
};

function defaultFormatTime(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function describeSeatConnection(
  state: ConnectionState,
  lastSyncedAt: number | null,
  formatTime: (ms: number) => string = defaultFormatTime,
): SeatConnectionStatus {
  const described = STATES[state];
  return {
    state,
    ...described,
    syncedAtLabel: lastSyncedAt === null ? null : formatTime(lastSyncedAt),
    ariaLabel: `Live seating updates: ${described.label}. ${described.detail}`,
  };
}

/**
 * Whether a change of connection state is a recovery worth confirming.
 *
 * Only a connection that was lost can be recovered. Coming up for the first
 * time is the connection simply working, and telling the admin it "came back"
 * would be a confirmation of something they never saw break.
 */
export function isConnectionRecovery(
  before: ConnectionState | null,
  after: ConnectionState,
): boolean {
  return before === 'disconnected' && after === 'connected';
}
